import {
  Component,
  HostListener,
  OnInit,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  ViewChildren,
  QueryList,
} from '@angular/core';
import {
  DomSanitizer,
  SafeHtml,
  SafeResourceUrl,
  SafeStyle,
} from '@angular/platform-browser';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { environment } from 'src/environments/environment';
import * as mammoth from 'mammoth';
import * as ExcelJS from 'exceljs';

@Component({
  selector: 'vl-document-preview',
  templateUrl: './document-preview.component.html',
  styleUrls: ['./document-preview.component.scss'],
})
export class DocumentPreviewComponent implements OnInit {
  // All files in the gallery
  files: any[] = [];
  // The file currently being displayed in the main preview
  currentFile: any;

  // State properties
  isLoading = false;
  storageBaseUrl = environment.storage + '/tk/';

  // Viewer-specific properties
  fileUrl: SafeResourceUrl | string | undefined;
  pdfSrc: string | Uint8Array = '';
  isImage: boolean = false;
  isPdf: boolean = false;
  isDocx: boolean = false;
  isXlsx: boolean = false;
  isTxt: boolean = false;
  isUnsupported: boolean = false;
  txtContent: string = '';
  docHtml: SafeHtml | null = null;

  excelProcessedData: { value: SafeHtml; style: SafeStyle }[][] = [];
  excelColumnWidths: number[] = [];
  excelRowHeights: number[] = [];


  @ViewChild('galleryScroller') galleryScroller!: ElementRef<HTMLDivElement>;
  @ViewChildren('galleryItem') galleryItems!: QueryList<ElementRef<HTMLDivElement>>;

  cacheFile = {} as any;

  constructor(
    private dialogService: DialogService,
    private sanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef // Inject ChangeDetectorRef
  ) {}

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'ArrowRight') {
      this.selectNext();
    } else if (event.key === 'ArrowLeft') {
      this.selectPrevious();
    }
  }

  ngOnInit(): void {
    const dialogData = this.dialogService.currentData;
    this.files = dialogData.files || [];
    const initialFileId = dialogData.currentFileId;

    if (initialFileId && this.files.length > 0) {
      const foundFile = this.files.find((f) => f._id === initialFileId);
      this.selectFile(foundFile || this.files[0]); // Select found file or fallback to the first
    } else if (this.files.length > 0) {
      this.selectFile(this.files[0]); // If no ID, select the first file
    }
  }

  selectFile(file: any): void {
    if (!file || this.isLoading) return;
    this.currentFile = file;
    this.displayFile();

    // Use a timeout to wait for the view to update with the .selected class
    setTimeout(() => {
      this.scrollToSelected();
    }, 0);
  }

  private scrollToSelected(): void {
    const selectedIndex = this.files.findIndex(
      (f) => f._id === this.currentFile._id
    );
    const selectedItem = this.galleryItems?.toArray()[selectedIndex];

    if (selectedItem) {
      selectedItem.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }

  selectNext(): void {
    const currentIndex = this.files.findIndex(
      (f) => f._id === this.currentFile._id
    );
    const nextIndex = currentIndex + 1;
    if (nextIndex < this.files.length) {
      this.selectFile(this.files[nextIndex]);
    }
  }

  selectPrevious(): void {
    const currentIndex = this.files.findIndex(
      (f) => f._id === this.currentFile._id
    );
    const prevIndex = currentIndex - 1;
    if (prevIndex >= 0) {
      this.selectFile(this.files[prevIndex]);
    }
  }

  private async displayFile(): Promise<void> {
    if (!this.currentFile) return;

    console.time('Tiempo de Carga Vista Previa'); // Start timer

    // Reset state and set loading flag
    this.resetViewers();
    this.isLoading = true;

    const unsafeUrl = this.storageBaseUrl + this.currentFile._id;
    const mimetype = this.currentFile.mimetype || '';

    this.isImage = mimetype.startsWith('image/');
    this.isPdf = mimetype === 'application/pdf';
    this.isDocx =
      mimetype ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    this.isXlsx =
      mimetype ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    this.isTxt = mimetype === 'text/plain';

    this.isUnsupported = !(
      this.isImage ||
      this.isPdf ||
      this.isDocx ||
      this.isXlsx ||
      this.isTxt
    );

    if (this.isUnsupported) {
      this.isLoading = false;
      console.timeEnd('Tiempo de Carga Vista Previa');
      return;
    }

    try {
      if (this.isImage) {
        this.fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(unsafeUrl);
      } else if (this.isPdf) {
        await this.renderPdf(unsafeUrl);
      } else if (this.isTxt) {
        await this.renderTxt(unsafeUrl);
      } else if (this.isDocx) {
        await this.renderDocx(unsafeUrl);
      } else if (this.isXlsx) {
        await this.renderXlsx(unsafeUrl);
      } else {
        this.txtContent = `No se pudo cargar la vista previa del archivo.`;
        this.isTxt = true; // Show error in text viewer
        this.docHtml = null; // Show error in text viewer
        this.isDocx = false
        this.isPdf = false
      }
    } catch (error) {
      console.error('Error rendering file preview:', error);
      this.txtContent = `No se pudo cargar la vista previa del archivo.`;
      this.isTxt = true; // Show error in text viewer
      this.docHtml = null; // Show error in text viewer
      this.isDocx = false
      this.isPdf = false
    } finally {
      this.isLoading = false;
      this.cdRef.detectChanges(); // Force change detection after content is loaded
      console.timeEnd('Tiempo de Carga Vista Previa'); // End timer
    }
  }

  private resetViewers(): void {
    this.isImage = this.isPdf = this.isDocx = this.isXlsx = this.isTxt = this.isUnsupported = false;
    this.fileUrl = undefined;
    this.pdfSrc = '';
    this.txtContent = '';
    this.excelProcessedData = [];
    this.excelColumnWidths = [];
    this.excelRowHeights = [];
  }

  private async renderPdf(url: string): Promise<void> {
    if (!this.cacheFile[url]) {
      console.time('renderPdf: Fetching');
      const response = await fetch(url);
      if (!response.ok)
        throw new Error(`Failed to fetch: ${response.statusText}`);
      console.timeEnd('renderPdf: Fetching');

      console.time('renderPdf: Processing');
      // Cache the ArrayBuffer, not a view on it.
      this.cacheFile[url] = await response.arrayBuffer();
      console.timeEnd('renderPdf: Processing');
    }

    // Create a new Uint8Array from a *copy* of the cached buffer each time.
    const cachedBuffer: ArrayBuffer = this.cacheFile[url];
    const bufferCopy = cachedBuffer.slice(0);
    this.pdfSrc = new Uint8Array(bufferCopy);

    this.cdRef.detectChanges();
  }

  private async renderTxt(url: string): Promise<void> {
    if (!this.cacheFile[url]) {

      console.time('renderTxt: Fetching');
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch: ${response.statusText}`);
      console.timeEnd('renderTxt: Fetching');

      console.time('renderTxt: Processing');
      const data = await response.text();
      this.cacheFile[url] = data

    }
    this.txtContent = this.cacheFile[url]
    this.cdRef.detectChanges(); // Force change detection
    console.timeEnd('renderTxt: Processing');
  }

  private async renderDocx(url: string): Promise<void> {
    if (!this.cacheFile[url]) {


      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch: ${response.statusText}`);
      const arrayBuffer = await response.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      const data = this.sanitizer.bypassSecurityTrustHtml(result.value);
      this.cacheFile[url] = data
    }
    this.docHtml = this.cacheFile[url]

    this.cdRef.detectChanges(); // Force change detection
  }

  private async renderXlsx(url: string): Promise<void> {
    if (!this.cacheFile[url]) {
      console.time('renderXlsx: Fetching');
      const response = await fetch(url);
      if (!response.ok)
        throw new Error(`Failed to fetch: ${response.statusText}`);
      const buffer = await response.arrayBuffer();
      console.timeEnd('renderXlsx: Fetching');

      console.time('renderXlsx: Processing');
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);

      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        this.cacheFile[url] = { data: [], colWidths: [], rowHeights: [] };
        this.excelProcessedData = [];
        this.excelColumnWidths = [];
        this.excelRowHeights = [];
        this.cdRef.detectChanges();
        return;
      }

      const colWidths: number[] = [];
      for (let i = 1; i <= worksheet.columnCount; i++) {
        const col = worksheet.getColumn(i);
        colWidths[i - 1] = col.width ? col.width * 8 : 80; // approx width + default
      }

      const rowHeights: number[] = [];
      const processedData: { value: SafeHtml; style: SafeStyle }[][] = [];
      worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        rowHeights[rowNumber - 1] = row.height ? row.height * 1.333 : 20; // pt to px + default

        const rowData: { value: SafeHtml; style: SafeStyle }[] = [];
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          rowData[colNumber - 1] = {
            value: this.sanitizer.bypassSecurityTrustHtml(
              this.getCellValue(cell)
            ),
            style: this.sanitizer.bypassSecurityTrustStyle(
              this.getCellStyle(cell)
            ),
          };
        });
        processedData[rowNumber - 1] = rowData;
      });

      // Pad rows to have the same number of cells
      const maxCols = processedData.reduce(
        (max, r) => Math.max(max, r.length),
        0
      );
      processedData.forEach((row) => {
        for (let i = 0; i < maxCols; i++) {
          if (!row[i]) {
            row[i] = {
              value: this.sanitizer.bypassSecurityTrustHtml(''),
              style: this.sanitizer.bypassSecurityTrustStyle(''),
            };
          }
        }
      });

      this.cacheFile[url] = {
        data: processedData,
        colWidths: colWidths,
        rowHeights: rowHeights,
      };
      console.timeEnd('renderXlsx: Processing');
    }

    this.excelProcessedData = this.cacheFile[url].data;
    this.excelColumnWidths = this.cacheFile[url].colWidths;
    this.excelRowHeights = this.cacheFile[url].rowHeights;
    this.cdRef.detectChanges();
  }

  private getCellValue(cell: ExcelJS.Cell): any {
    let currentCell = cell;
    const visited = new Set<string>();
    while (
      currentCell.isMerged &&
      currentCell.master &&
      !visited.has(currentCell.address)
    ) {
      visited.add(currentCell.address);
      currentCell = currentCell.master;
    }

    if (currentCell.value) {
      if (currentCell.value.hasOwnProperty('hyperlink')) {
        const hyperlinkValue = currentCell.value as ExcelJS.CellHyperlinkValue;
        return `<a href="${hyperlinkValue.hyperlink}" target="_blank">${hyperlinkValue.text}</a>`;
      }
      if (currentCell.value.hasOwnProperty('richText')) {
        const richTextValue = currentCell.value as ExcelJS.CellRichTextValue;
        return richTextValue.richText.map((rt) => rt.text).join('');
      }
      if (currentCell.value.hasOwnProperty('result')) {
        // formula
        return (currentCell.value as ExcelJS.CellFormulaValue).result;
      }
    }
    return currentCell.text || '';
  }

  private getCellStyle(cell: ExcelJS.Cell): string {
    let currentCell = cell;
    const visited = new Set<string>();
    while (
      currentCell.isMerged &&
      currentCell.master &&
      !visited.has(currentCell.address)
    ) {
      visited.add(currentCell.address);
      currentCell = currentCell.master;
    }

    const styles: string[] = [];

    // Font
    if (currentCell.font) {
      if (currentCell.font.bold) styles.push('font-weight: bold;');
      if (currentCell.font.italic) styles.push('font-style: italic;');
      if (currentCell.font.underline) styles.push('text-decoration: underline;');
      if (currentCell.font.size)
        styles.push(`font-size: ${currentCell.font.size * 0.75}pt;`); // convert to pt
      if (currentCell.font.name)
        styles.push(`font-family: ${currentCell.font.name};`);
      if (currentCell.font.color?.argb)
        styles.push(`color: #${currentCell.font.color.argb.slice(-6)};`);
    }

    // Fill
    if (
      currentCell.fill?.type === 'pattern' &&
      currentCell.fill.pattern === 'solid' &&
      currentCell.fill.fgColor?.argb
    ) {
      const argb = currentCell.fill.fgColor.argb;
      if (argb.length === 8) {
        const alpha = parseInt(argb.substring(0, 2), 16) / 255;
        const rgb = argb.substring(2);
        styles.push(
          `background-color: rgba(${parseInt(
            rgb.substring(0, 2),
            16
          )}, ${parseInt(rgb.substring(2, 4), 16)}, ${parseInt(
            rgb.substring(4, 6),
            16
          )}, ${alpha});`
        );
      } else {
        styles.push(`background-color: #${argb.slice(-6)};`);
      }
    }

    // Alignment
    if (currentCell.alignment) {
      if (currentCell.alignment.horizontal)
        styles.push(`text-align: ${currentCell.alignment.horizontal};`);
      if (currentCell.alignment.vertical)
        styles.push(`vertical-align: ${currentCell.alignment.vertical};`);
      if (currentCell.alignment.wrapText) styles.push('white-space: pre-wrap;');
    }

    // Borders
    const borderToCss = (side: string, border: Partial<ExcelJS.Border>) => {
      if (border && border.style && border.color?.argb) {
        const style = border.style === 'thin' ? 'solid' : border.style;
        const color = `#${border.color.argb.slice(-6)}`;
        return `border-${side}: 1px ${style} ${color};`;
      }
      return '';
    };

    if (currentCell.border) {
      if (currentCell.border.top)
        styles.push(borderToCss('top', currentCell.border.top));
      if (currentCell.border.left)
        styles.push(borderToCss('left', currentCell.border.left));
      if (currentCell.border.bottom)
        styles.push(borderToCss('bottom', currentCell.border.bottom));
      if (currentCell.border.right)
        styles.push(borderToCss('right', currentCell.border.right));
    }

    return styles.join(' ');
  }

  getType(
    name: string
  ):
    | 'pdf'
    | 'image'
    | 'docx'
    | 'csv'
    | 'xlsx'
    | 'xls'
    | 'txt'
    | 'email'
    | 'other' {
    if (!name) return 'other';
    const ext = name.split('.').pop()?.toLowerCase() || '';
    if (ext == 'pdf') return 'pdf';
    if (ext == 'docx') return 'docx';
    if (ext == 'csv') return 'csv';
    if (ext == 'xlsx' || ext == 'xls') return 'xlsx';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext))
      return 'image';
    if (['eml', 'emlx', 'msg', 'msgx'].includes(ext)) return 'email';
    if (ext == 'txt') return 'txt';
    return 'other';
  }

  close(): void {
    this.dialogService.close();
  }
}


